import React, { useEffect, useRef, useState } from 'react';
import SectionTag from '../components/SectionTag';
import { FiHeart, FiArrowDown } from 'react-icons/fi';

function useMagnetic() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width  / 2) * 0.28;
      const y = (e.clientY - r.top  - r.height / 2) * 0.28;
      el.style.transition = 'transform 100ms ease';
      el.style.transform  = `translate(${x}px, ${y}px)`;
    };
    const onLeave = () => {
      el.style.transition = 'transform 500ms cubic-bezier(0.34,1.56,0.64,1)';
      el.style.transform  = 'translate(0,0)';
    };
    el.addEventListener('mousemove',  onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => { el.removeEventListener('mousemove', onMove); el.removeEventListener('mouseleave', onLeave); };
  }, []);
  return ref;
}

function StatItem({ value, label }) {
  const ref = useRef(null);
  const [displayed, setDisplayed] = useState('0+');
  const num = parseInt(value);
  const suffix = value.replace(/[0-9]/g, '');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(el);
        const duration = 1400;
        const startTime = performance.now();
        const tick = (now) => {
          const t = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplayed(Math.floor(eased * num) + suffix);
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [num, suffix]);

  return (
    <div ref={ref}>
      <div style={{ fontFamily: 'popinsBold', fontSize: '20px', color: '#1B1A28' }}>{displayed}</div>
      <div style={{ fontFamily: 'popinsRegular', fontSize: '13px', color: '#6B7280' }}>{label}</div>
    </div>
  );
}

const STATS = [
  { value: '50+', label: 'Non Profit' },
  { value: '200+', label: 'Resources' },
  { value: '500+', label: 'People Helped' },
];

function HeroChild({ children, delay, fromLeft }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = fromLeft ? 'translateX(-50px)' : 'translateX(50px)';
    el.style.transition = `opacity 600ms ease ${delay}ms, transform 600ms ease ${delay}ms`;
    setTimeout(() => {
      if (el) { el.style.opacity = '1'; el.style.transform = 'translate(0,0)'; }
    }, 80);
  }, [delay, fromLeft]);
  return <div ref={ref} style={{ willChange: 'transform, opacity' }}>{children}</div>;
}

export default function Hero() {
  const magBtn1 = useMagnetic();
  const magBtn2 = useMagnetic();
  const scrollIndicatorRef = useRef(null);

  useEffect(() => {
    const indicator = scrollIndicatorRef.current;
    if (!indicator) return;
    const onScroll = () => {
      const opacity = Math.max(0, 1 - window.scrollY / 120);
      indicator.style.opacity = String(opacity * 0.5);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#ffffff', paddingTop: '80px' }}
    >
      <div className="amb-orb amb-orb-1" />
      <div className="amb-orb amb-orb-2" />
      <div className="amb-orb amb-orb-3" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(63,196,227,0.25) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          opacity: 0.4,
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%' }}>
          <path d="M0 60L1440 60L1440 30C1200 60 960 0 720 15C480 30 240 60 0 30L0 60Z" fill="#F8FAFC" />
        </svg>
      </div>

      <div className="relative z-10 w-full max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center hero-content">
        {/* Left */}
        <div className="flex flex-col gap-6">
          <HeroChild delay={0} fromLeft>
            <SectionTag label="NON-PROFIT PLATFORM" icon={<FiHeart size={12} />} />
          </HeroChild>

          <HeroChild delay={150} fromLeft>
            <h1 style={{ fontFamily: 'popinsBold', fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.1 }}>
              <span className="shimmer-heading">Connecting</span>{' '}
              <span className="shimmer-heading-cyan">Communities</span>,<br />
              <span className="shimmer-heading">One Resource at a Time.</span>
            </h1>
          </HeroChild>

          <HeroChild delay={300} fromLeft>
            <p style={{ fontFamily: 'popinsRegular', fontSize: '17px', color: '#6B7280', maxWidth: '480px', lineHeight: '1.8' }}>
              TCCplus bridges the gap between non-profit organizations and the people who need them most — making community support accessible, visible, and immediate.
            </p>
          </HeroChild>

          <HeroChild delay={450} fromLeft>
            <div className="flex items-center gap-4 flex-wrap">
              <a
                ref={magBtn1}
                href="#nonprofits"
                style={{
                  height: '48px', padding: '0 28px', display: 'inline-flex', alignItems: 'center',
                  border: '1.5px solid #3FC4E3', color: '#3FC4E3', borderRadius: '10px',
                  fontFamily: 'popinsSemiBold', fontSize: '16px', textDecoration: 'none',
                  transition: 'background 300ms ease', background: 'transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(63,196,227,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                For Non Profit →
              </a>
              <a
                ref={magBtn2}
                href="#people-in-need"
                style={{
                  height: '48px', padding: '0 28px', display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: 'linear-gradient(135deg, #3FC4E3, #006077)',
                  color: '#ffffff', borderRadius: '10px',
                  fontFamily: 'popinsSemiBold', fontSize: '16px', textDecoration: 'none',
                  boxShadow: '0 4px 16px rgba(63,196,227,0.3)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(63,196,227,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(63,196,227,0.3)'; }}
              >
                Get the App — iOS & Android <FiArrowDown size={16} />
              </a>
            </div>
          </HeroChild>

          <HeroChild delay={600} fromLeft>
            <div className="flex items-center gap-8 flex-wrap mt-2">
              {STATS.map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <div style={{ width: '1px', height: '40px', background: '#E5E5E5' }} />}
                  <StatItem value={s.value} label={s.label} />
                </React.Fragment>
              ))}
            </div>
          </HeroChild>
        </div>

        {/* Right */}
        <HeroChild delay={300} fromLeft={false}>
          <div className="relative flex justify-center">
            <div
              className="absolute blur-3xl rounded-full pointer-events-none"
              style={{ width: '80%', height: '80%', background: 'rgba(63,196,227,0.15)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 0 }}
            />
            <img
              src={`${process.env.PUBLIC_URL}/hero-illustration.png`}
              alt="Community connecting through TCCplus"
              loading="eager"
              className="float-animation relative z-10"
              style={{
                width: '100%',
                maxWidth: '480px',
                height: 'auto',
                willChange: 'transform',
                filter: 'drop-shadow(0 24px 48px rgba(63,196,227,0.25))',
              }}
            />
          </div>
        </HeroChild>
      </div>

      {/* Scroll indicator */}
      <div
        ref={scrollIndicatorRef}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ opacity: 0.5, transition: 'opacity 200ms ease' }}
      >
        <div style={{ width: '24px', height: '38px', border: '2px solid #3FC4E3', borderRadius: '12px', display: 'flex', justifyContent: 'center', paddingTop: '6px' }}>
          <div className="scroll-dot" style={{ width: '4px', height: '8px', background: '#3FC4E3', borderRadius: '2px' }} />
        </div>
        <span style={{ fontFamily: 'popinsRegular', fontSize: '12px', color: '#9CA3AF' }}>Scroll to explore</span>
      </div>
    </section>
  );
}
